import { Avatar, Box, Card, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { mediaSourceUrl } from './utils'
import { IconProjectPlaceholder } from '../../../icons/IconProjectPlaceholder'
import {
  createNotificationNavHandlers,
  getActorPathKey,
  getProjectSlug,
  getSocialOrderSlug,
} from './notificationNav'
import { ActorNameSpan, ProjectNameQuoted } from './notificationTypography'
import { notificationBodyWrapSx } from './notificationTextClamp'

type Props = {
  notification: Extract<
    KnownNotificationResource,
    { type: 'social_order.linked_project.created' | 'social_order.linked_project.created.author' }
  >
}

export function SocialOrderLinkedProjectCreatedNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { goToProject, onProjectClick, onUserClick, onSocialOrderClick } =
    createNotificationNavHandlers(navigate)
  const payload = notification.payload
  const project = payload.project
  const socialOrder = payload.social_order
  const actor = payload.actor
  const forAuthor = notification.type === 'social_order.linked_project.created.author'

  const projectTitle =
    typeof project?.title === 'string' && project.title.trim().length > 0 ? project.title : 'проект'
  const socialOrderTitle =
    typeof socialOrder?.title === 'string' && socialOrder.title.trim().length > 0
      ? socialOrder.title
      : 'соцзаказ'
  const actorName =
    typeof actor?.fullname === 'string' && actor.fullname.trim().length > 0 ? actor.fullname : 'Пользователь'
  const projectAvatarSrc = mediaSourceUrl(project?.image_url) ?? undefined
  const projectSlug = getProjectSlug(project)
  const socialOrderSlug = getSocialOrderSlug(socialOrder)
  const actorPathKey = getActorPathKey(actor, notification.actor_user_id)

  return (
    <Card
      variant="primaryAdaptiveCard"
      sx={{ p: 2, cursor: projectSlug ? 'pointer' : 'default' }}
      onClick={() => goToProject(projectSlug)}
    >
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onProjectClick(e, projectSlug)}
          sx={{ cursor: projectSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={projectTitle}
            avatarSrc={projectAvatarSrc}
            fallbackIcon={<IconProjectPlaceholder fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle={forAuthor ? 'Проект по вашему соцзаказу' : 'Проект по соцзаказу'}
          />
        </Box>

        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          <Box
            onClick={actorPathKey ? (e) => onUserClick(e, actorPathKey) : undefined}
            sx={{ cursor: actorPathKey ? 'pointer' : 'default', flexShrink: 0 }}
          >
            <Avatar
              src={mediaSourceUrl(actor?.avatar_url) ?? undefined}
              alt={actorName}
              sx={{ width: 34, height: 34 }}
            />
          </Box>
          <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
            <ActorNameSpan pathKey={actorPathKey} onUserClick={onUserClick}>
              {actorName}
            </ActorNameSpan>
            {' опубликовал(а) проект '}
            <ProjectNameQuoted
              name={projectTitle}
              pathKey={projectSlug}
              onNameClick={onProjectClick}
            />
            {forAuthor ? ' по вашему соцзаказу ' : ' по соцзаказу '}
            <ProjectNameQuoted
              name={socialOrderTitle}
              pathKey={socialOrderSlug}
              onNameClick={onSocialOrderClick}
            />
          </Typography>
        </Stack>

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} •{' '}
          {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
